import { CoralogixRum, CoralogixLogSeverity } from '@coralogix/browser';

// Coralogix configuration
const CORALOGIX_CONFIG = {
  publicKey: process.env.REACT_APP_CORALOGIX_PUBLIC_KEY || '',
  application: process.env.REACT_APP_CORALOGIX_APPLICATION_NAME || 'back-office-client',
  coralogixDomain: 'US2' as const, // or 'EU2', 'US1', 'US2', 'AP1', 'AP2', 'AP3', 'STAGING'
  version: process.env.REACT_APP_VERSION || '1.0.0',
  environment: process.env.REACT_APP_ENV || 'development',
};

// Initialize Coralogix Logger
export const initCoralogixLogger = () => {
  if (!CORALOGIX_CONFIG.publicKey) {
    console.warn('Coralogix Logger not initialized: Missing public key');
    return;
  }

  try {
    CoralogixRum.init({
      public_key: CORALOGIX_CONFIG.publicKey,
      application: CORALOGIX_CONFIG.application,
      coralogixDomain: CORALOGIX_CONFIG.coralogixDomain,
      version: CORALOGIX_CONFIG.version,
      environment: CORALOGIX_CONFIG.environment,
      instrumentations: {
        fetch: false,
        xhr: false,
        long_tasks: false,
        resources: false,
        interactions: false,
        web_vitals: false,
      }
    });

    // Set up global error handler to capture unhandled errors
    window.addEventListener('error', (event) => {
      // Only log if it's not already handled by our custom logging
      if (!event.error?.__coralogixHandled) {
        const error = event.error || new Error(event.message);
        logError('Unhandled error', error, {
          source: 'window.onerror',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      }
    });

    // Set up unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      // Only log if it's not already handled by our custom logging
      if (!event.reason?.__coralogixHandled) {
        logError('Unhandled promise rejection', new Error(event.reason), {
          source: 'unhandledrejection',
          reason: event.reason,
        });
      }
    });

    console.log('Coralogix Logger initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Coralogix Logger:', error);
  }
};

// Helper functions for logging
export const logInfo = (message: string, context?: object) => {
  const logData = {
    message,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...context,
  };
  
  CoralogixRum.info(message, logData);
};

export const logWarn = (message: string, context?: object) => {
  const logData = {
    message,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...context,
  };
  
  CoralogixRum.warn(message, logData);
};

// Utility function to create proper error objects with stack traces
export const createErrorWithStack = (error: any, context?: string): Error => {
  let errorObj: Error;
  
  if (error instanceof Error) {
    errorObj = error;
  } else if (typeof error === 'string') {
    errorObj = new Error(error);
  } else if (error && typeof error === 'object') {
    errorObj = new Error(error.message || 'Unknown error');
    if (error.stack) {
      errorObj.stack = error.stack;
    }
  } else {
    errorObj = new Error(String(error));
  }
  
  // Add context to the error message if provided
  if (context) {
    errorObj.message = `[${context}] ${errorObj.message}`;
  }
  
  return errorObj;
};

// Enhanced error logging with automatic error object creation
export const logErrorWithContext = (message: string, error: any, context?: object, errorContext?: string) => {
  const errorObj = createErrorWithStack(error, errorContext);
  logError(message, errorObj, context);
};

export const logError = (message: string, error?: Error | any, context?: object) => {
  // Ensure we have a proper error object with stack trace
  let errorMessage = '';
  let stackTrace = '';
  
  if (error) {
    if (error instanceof Error) {
      errorMessage = error.message;
      stackTrace = error.stack || '';
      // Mark as handled to prevent duplicate logging
      (error as any).__coralogixHandled = true;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && typeof error === 'object') {
      errorMessage = error.message || error.toString();
      stackTrace = error.stack || '';
      // Mark as handled to prevent duplicate logging
      error.__coralogixHandled = true;
    } else {
      errorMessage = String(error);
    }
  }

  // Create a more detailed error context
  const errorContext = {
    message,
    error: errorMessage,
    stack: stackTrace,
    errorType: error?.constructor?.name || typeof error,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    // Add source map hints
    sourceMapAvailable: true,
    ...context,
  };

  // Log to Coralogix
  CoralogixRum.error(message, errorContext);
  
  // Also log to console for debugging
  console.error('Coralogix Error Log:', errorContext);
};

// User action tracking (equivalent to RUM actions)
export const trackUserAction = (name: string, attributes?: object) => {
  const actionData = {
    message: `User Action: ${name}`,
    action: name,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...attributes,
  };
  
  CoralogixRum.info(`User Action: ${name}`, actionData);
};

// View tracking (equivalent to RUM views)
export const trackView = (name: string, attributes?: object) => {
  const viewData = {
    message: `View: ${name}`,
    view: name,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...attributes,
  };
  
  CoralogixRum.info(`View: ${name}`, viewData);
};

// Set user information
export const setUser = (user: { id: string; name?: string; email?: string; [key: string]: any }) => {
  // Use Coralogix's setUserContext method
  CoralogixRum.setUserContext({
    user_id: user.id,
    user_name: user.name || '',
    user_email: user.email,
    user_metadata: Object.fromEntries(
      Object.entries(user).filter(([key]) => !['id', 'name', 'email'].includes(key))
    ),
  });
};

// Set global context
export const setGlobalContext = (context: object) => {
  // Use Coralogix's setApplicationContext method
  CoralogixRum.setApplicationContext({
    application: CORALOGIX_CONFIG.application,
    version: CORALOGIX_CONFIG.version,
    ...context,
  });
};

// Test function to verify stack trace logging
export const testStackTraceLogging = () => {
  try {
    // Create a test error with a stack trace
    const testError = new Error('Test error for stack trace verification');
    testError.stack = testError.stack || 'Test stack trace';
    
    logError('Test error logging', testError, {
      test: true,
      timestamp: new Date().toISOString(),
    });
    
    console.log('Test error logged to Coralogix. Check your Coralogix dashboard for the stack trace.');
  } catch (error) {
    console.error('Failed to test stack trace logging:', error);
  }
};

// Comprehensive test for source map integration
export const testSourceMapIntegration = () => {
  console.log('Testing Coralogix source map integration...');
  
  // Test 1: Basic error with stack trace
  const basicError = new Error('Source map test error');
  logError('Source map test - basic error', basicError, { testType: 'source_map_basic' });
  
  // Test 2: Error from a specific function call
  const functionError = new Error('Function call error');
  functionError.stack = `Error: Function call error
    at testSourceMapIntegration (${window.location.origin}/src/utils/coralogix.ts:123:45)
    at onClick (${window.location.origin}/src/components/CoralogixTest.tsx:15:23)
    at HTMLButtonElement.callCallback (${window.location.origin}/src/components/CoralogixTest.tsx:25:67)`;
  
  logError('Source map test - function error', functionError, { testType: 'source_map_function' });
  
  // Test 3: Async error
  setTimeout(() => {
    const asyncError = new Error('Async source map test error');
    logError('Source map test - async error', asyncError, { testType: 'source_map_async' });
  }, 100);
  
  console.log('Source map integration tests completed. Check Coralogix dashboard for results.');
}; 